/**
 * CreateEntityCommand - Generic command for creating entities (StepNode, StrategyBlock)
 *
 * Implements the Command pattern for undoable entity creation.
 * Delegates to FlowCoordinator for actual creation logic.
 *
 * Generic Architecture:
 * - TEntity extends FlowEntity (StepNode, StrategyBlock)
 * - TService (StepNodeScreenService, StrategyBlockService)
 * - Uses ONLY generic coordinator methods (createEntity, deleteEntities)
 *
 * Usage:
 * ```typescript
 * // For V2 StepNode
 * const command = new CreateEntityCommand<FlowStepNode, StepNodeScreenService>(
 *   { templateId: 'template-1' },
 *   { x: 100, y: 200 },
 *   screenContext
 * );
 *
 * // For Block
 * const command = new CreateEntityCommand<StrategyBlock, StrategyBlockService>(
 *   { name: 'My Block', blockType: 'UserInput' },
 *   { x: 100, y: 200 },
 *   blockContext
 * );
 *
 * await commandManager.execute(command);
 * ```
 *
 * Related Requirements:
 * - FR-P1-003: Commands use coordinator ONLY for all operations
 * - FR-P3-001: Generic FlowCoordinator interface
 * - FR-P3-004: Generic CommandContext
 */

import { Command, CommandResult } from './Command';
import { CommandContext } from './CommandContext';
import type { FlowEntity, PositionOnConfigurator } from '@modules/reactFlow/types/flow-canvas-types';

/**
 * CreateEntityCommand - Generic command for creating flow entities
 *
 * @template TEntity - Entity type extending FlowEntity (StepNode, StrategyBlock)
 * @template TService - Firebase service type (StepNodeScreenService, StrategyBlockService)
 */
export class CreateEntityCommand<
  TEntity extends FlowEntity,
  TService
> implements Command {
  public readonly id: string;
  public readonly description: string;
  public readonly timestamp: number;

  private createdEntityId?: string;

  /**
   * @param entityData - Partial entity data (without id and position)
   * @param position - Position on canvas
   * @param context - Command execution context
   */
  constructor(
    private entityData: Partial<TEntity>,
    private position: PositionOnConfigurator,
    private context: CommandContext<TEntity, TService>
  ) {
    this.id = `create-entity-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    this.description = `Create entity at (${position.x}, ${position.y})`;
    this.timestamp = Date.now();
  }

  /**
   * Execute command - Create entity via coordinator
   *
   * Flow:
   * 1. Call coordinator.createEntity() - handles Registry + Canvas + Firebase
   * 2. Store created entity ID for undo
   * 3. Return success result
   *
   * @returns Promise resolving with command result
   */
  async execute(): Promise<CommandResult> {
    try {
      console.log('[CreateEntityCommand] Executing create entity');

      // Coordinator handles EVERYTHING (Registry + Canvas + Firebase)
      const result = await this.context.coordinator.createEntity(
        this.entityData,
        {
          x: this.position.x ?? 0,
          y: this.position.y ?? 0
        }
      );

      this.createdEntityId = result.entity.entityId;

      console.log(`[CreateEntityCommand] ✅ Entity created: ${this.createdEntityId}`);

      return {
        success: true,
        affectedEntities: { screens: [this.createdEntityId!] }
      };
    } catch (error) {
      console.error('[CreateEntityCommand] ❌ Create failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error : new Error('Unknown error'),
        affectedEntities: {}
      };
    }
  }

  /**
   * Undo command - Delete created entity via coordinator
   *
   * Flow:
   * 1. Call coordinator.deleteEntities() - handles Registry + Canvas + Firebase
   * 2. Return success result
   *
   * @returns Promise resolving with command result
   */
  async undo(): Promise<CommandResult> {
    if (!this.createdEntityId) {
      return {
        success: false,
        error: new Error('No created entity to undo'),
        affectedEntities: {}
      };
    }

    try {
      console.log(`[CreateEntityCommand] Undoing create for entity: ${this.createdEntityId}`);

      // Coordinator handles EVERYTHING (Registry + Canvas + Firebase)
      await this.context.coordinator.deleteEntities([this.createdEntityId]);

      console.log('[CreateEntityCommand] ✅ Undo successful');

      return {
        success: true,
        affectedEntities: { screens: [this.createdEntityId] }
      };
    } catch (error) {
      console.error('[CreateEntityCommand] ❌ Undo failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error : new Error('Undo failed'),
        affectedEntities: {}
      };
    }
  }

  /**
   * Check if command can be executed
   * @returns Always true (creation always possible)
   */
  canExecute(): boolean {
    return true;
  }

  /**
   * Check if command can be undone
   * @returns true if entity was created successfully
   */
  canUndo(): boolean {
    return !!this.createdEntityId;
  }
}
